import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface Video {
  id: string;
  title: string;
  description: string;
  source: string;
  source_id: string;
  source_url: string;
  thumbnail_url: string;
  duration_seconds: number;
  speaker: string;
  series: string;
  tags: string;
  transcript_status: 'pending' | 'processing' | 'completed' | 'failed';
  transcript_r2_key: string;
  chunk_count: number;
  published_at: string;
  created_at: string;
  updated_at: string;
}

interface SearchChunk {
  id: string;
  chunk_index: number;
  content: string;
  start_time: number;
  end_time: number;
  start_segment_index: number | null;
  end_segment_index: number | null;
}

export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentTime, setCurrentTime] = useState<number>(0);

  const { data: video, isLoading } = useQuery({
    queryKey: ['video', id],
    queryFn: () => api.get<{ data: Video }>(`/videos/${id}`),
    enabled: !!id,
    // While this video is mid-transcribe, poll so the status flips to
    // completed/failed without a manual refresh.
    refetchInterval: (query) => {
      const status = query.state.data?.data?.transcript_status;
      return status === 'pending' || status === 'processing' ? 3000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const { data: chunks } = useQuery({
    queryKey: ['video-chunks', id],
    queryFn: () => api.get<{ data: SearchChunk[] }>(`/videos/${id}/chunks`),
    enabled: !!id && video?.data?.transcript_status === 'completed',
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      navigate('/videos');
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.post(`/videos/${id}/retranscribe`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video', id] });
    },
  });

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    processing: 'bg-yellow-100 text-yellow-800 animate-pulse',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!video?.data) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900">Video not found</h2>
        <Link to="/videos" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to videos
        </Link>
      </div>
    );
  }

  const v = video.data;
  const chunkCount = chunks?.data?.length || 0;

  return (
    <div>
      {/* Header with Back link and Action buttons */}
      <div className="mb-6 flex items-center justify-between">
        <Link to="/videos" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to Videos
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={v.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            View on {v.source}
          </a>
          {v.transcript_status === 'failed' && (
            <button
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
              className="btn-primary"
            >
              {retryMutation.isPending ? 'Retrying...' : 'Retry Transcription'}
            </button>
          )}
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this video?')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            className="btn-danger"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content - Video */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="aspect-video bg-gray-900 rounded-t-lg overflow-hidden">
              {v.source === 'youtube' && v.source_id ? (
                <iframe
                  key={currentTime} // Force re-render when timestamp changes
                  src={`https://www.youtube.com/embed/${v.source_id}?start=${Math.floor(currentTime)}&autoplay=${currentTime > 0 ? 1 : 0}`}
                  title={v.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : v.thumbnail_url ? (
                <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <span className="text-gray-400">No video available</span>
                </div>
              )}
            </div>
            <div className="p-6">
              <h1 className="text-xl font-bold text-gray-900">{v.title}</h1>
              {v.speaker && (
                <p className="mt-1 text-gray-600">{v.speaker}</p>
              )}
              {v.description && (
                <p className="mt-4 text-sm text-gray-500">{v.description}</p>
              )}
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                <span className="capitalize">{v.source}</span>
                {v.duration_seconds && <span>{formatDuration(v.duration_seconds)}</span>}
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[v.transcript_status]}`}>
                  {v.transcript_status === 'processing' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                  )}
                  {v.transcript_status === 'processing' ? 'transcribing' : v.transcript_status}
                </span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Details</h3>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Source ID</dt>
                <dd className="text-gray-900 font-mono text-xs mt-1">{v.source_id}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Chunks</dt>
                <dd className="text-gray-900 mt-1">{chunkCount}</dd>
              </div>
              {v.series && (
                <div>
                  <dt className="text-gray-500">Series</dt>
                  <dd className="text-gray-900 mt-1">{v.series}</dd>
                </div>
              )}
              {v.published_at && (
                <div>
                  <dt className="text-gray-500">Published</dt>
                  <dd className="text-gray-900 mt-1">{new Date(v.published_at).toLocaleDateString()}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Added</dt>
                <dd className="text-gray-900 mt-1">{new Date(v.created_at).toLocaleDateString()}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Sidebar - Transcript chunks */}
        <div className="lg:col-span-1">
          {v.transcript_status === 'completed' && chunks?.data && chunks.data.length > 0 ? (
            <div className="card">
              <div className="border-b border-gray-200 px-4 py-3 sticky top-0 bg-white rounded-t-lg z-10">
                <h2 className="text-sm font-semibold text-gray-900">
                  Transcript ({chunkCount} chunks)
                </h2>
                <p className="text-xs text-gray-500 mt-1">Click timestamp to jump to that moment</p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[calc(100vh-200px)] overflow-y-auto">
                {chunks.data.map((chunk) => (
                  <div key={chunk.id} className="p-3 hover:bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        onClick={() => setCurrentTime(chunk.start_time)}
                        className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded hover:bg-primary-100 transition-colors cursor-pointer"
                        title={`Jump to ${formatTimestamp(chunk.start_time)}`}
                      >
                        {formatTimestamp(chunk.start_time)}
                      </button>
                      <span className="text-xs text-gray-400">
                        - {formatTimestamp(chunk.end_time)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-4">{chunk.content}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : v.transcript_status === 'completed' ? (
            <div className="card p-6 text-center text-gray-500">
              <p className="text-sm">No transcript chunks available</p>
            </div>
          ) : v.transcript_status === 'pending' || v.transcript_status === 'processing' ? (
            <div className="card p-6 text-center">
              <LoadingSpinner size="md" />
              <p className="text-sm text-gray-500 mt-2">Processing transcript...</p>
            </div>
          ) : (
            <div className="card p-6 text-center text-gray-500">
              <p className="text-sm">Transcript not available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
